import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { postAuthorizeBridge } from 'services/oauthBridge';
import { getPortalJwt } from 'services/auth';
import refreshExpiredToken from 'hooks/xhr/refreshToken';

const MCP_BASE_URL = '/mcp';

/**
 * Bridge route between the chat host's OAuth flow and the MCP authorization server.
 * Reads OAuth query params, posts them with the portal JWT to the MCP bridge endpoint,
 * and navigates to the returned redirect URL (which routes the user back to the chat host).
 */
const ConnectChat = props => {
  const location = useLocation();

  useEffect(() => {
    const run = async () => {
      const portalJwt = getPortalJwt();
      const search = new URLSearchParams(location.search);

      // Match the portal's existing login convention (see AuthedRoute): pass the
      // target path in `redirect`, with the rest of the OAuth params alongside.
      // Login.js reconstructs <redirect>?<remaining-params> after authentication.
      const goToLogin = () => {
        const loginParams = new URLSearchParams(search);
        loginParams.set('redirect', location.pathname);
        window.location.href = `/login?${loginParams.toString()}`;
      };

      if (!portalJwt) {
        goToLogin();
        return;
      }

      const params = {
        state: search.get('state') ?? '',
        client_id: search.get('client_id') ?? '',
        redirect_uri: search.get('redirect_uri') ?? '',
        code_challenge: search.get('code_challenge') ?? '',
        code_challenge_method: search.get('code_challenge_method') ?? 'S256',
        scope: search.get('scope') ?? 'scileads:search',
        // RFC 8707: forward the resource indicator so the MCP server can bind the
        // issued access token to the correct audience.
        resource: search.get('resource') ?? undefined,
      };

      const submitBridge = jwt =>
        postAuthorizeBridge({
          mcpBaseUrl: MCP_BASE_URL,
          portalJwt: jwt,
          params,
        });

      try {
        let redirectUrl;
        try {
          redirectUrl = await submitBridge(portalJwt);
        } catch (error) {
          if (error.status !== 401) throw error;

          // The access token in localStorage is present but expired. Mirror the
          // app's XHR layer (useXHR): refresh once and retry, rather than
          // dead-ending on the error page. getPortalJwt is presence-only, so an
          // expired token reaches the bridge and 401s without this.
          let freshJwt;
          try {
            ({ token: freshJwt } = await refreshExpiredToken());
          } catch {
            // Refresh failed — the session is gone. Re-authenticate, preserving
            // the OAuth params, the same as the no-token path above.
            goToLogin();
            return;
          }
          redirectUrl = await submitBridge(freshJwt);
        }
        window.location.href = redirectUrl;
      } catch (error) {
        const status = error.status ? `?status=${error.status}` : '';
        window.location.href = `/connect-chat-error${status}`;
      }
    };

    run();
  }, [location]);

  return <div>Connecting your chat host to SciLeads…</div>;
};

export default ConnectChat;
